import {
    collection,
    CollectionReference,
    DocumentData,
    Timestamp,
    getCountFromServer,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    increment,
    arrayUnion,
    getDocs,
    query,
    where,
    limit,
    orderBy,
    startAfter,
    endBefore,
    limitToLast,
    DocumentSnapshot,
    startAt
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { startAuthentication } from "@simplewebauthn/browser";
import { fs, functions } from "../firebase.ts";
import { getDeviceID } from "../components/global.ts";

export interface Token {
    id?: string; // doc id => b.333.eco/t/id
    date?: Timestamp;
    from?: string;
    to: string;

    isAnonymous: boolean; // default = true
    token: string; // rainbow
    gold: string; // thank you note
    jewel: string; // photo cover art // url to storage or stock
    diamond: string; // url to video in storage
    type: "photo" | "short" | "ai" | "stock"; // media type: video/mp4 or image/jpeg // store type in file storage

    karat?: number; // int: number of words
    carat?: number; //video length; photo = 1; no photo = 0;
    kiitos?: number; // total int

    duration?: number; // stored: short length in minutes; // good for analytics
    fromBalance?: number; // not stored; for alert
    toBalance?: number; // not stored; for alert
    thankerBalance?: number;

    // for now: can only re-thank 1 time
    redates?: Timestamp[];
    rethankers?: string[]; // uid
    retokens?: string[];
    rekiitos?: number[]; // kiitos
    iskiitos?: boolean[]; // todo later: is nearby
    isthanks?: boolean[]; // to sender or to recipient //TODO: rename to isthankers?
    // for now: inherit isAnonymous from parent // NO: anonymous if tip else public
    // no gold or jewel bc can just create new thank
    // later: broadcast to followers like twitter

    tip: number; // recipient account: total cents // tip
    jar: number; // recipient fund: total cents // gratuity
    kitty?: number; // sender fund: total cents // donation // initally 0
    pool?: number; // sender pool: total cents // contribution // initally 0
    retips?: number[]; // tip + kitty // cents // always anonymous if not 0
    //rejars?: number[];
    //rekities?: number[];
    repools?: number[]; // jar + pool

    // TODO: later add more fields for all stamp types: see Chests
    //latitude: number;
    //longitude: number;
    //address: string; // human readable address

    // stats: from here
    digs?: number; // total digs
    diggers?: string[]; // device id of diggers
    miners?: string[]; // uid of miners

    // stats: b app
    previews?: number;
    crawlers?: Array<string>;
    scans?: number; // total scans
    scanners?: string[]; // device id of scanners
}

type TokenResponse = {
    text: string;
    color: "green" | "red" | "orange"; // status
    data?: Token;
};

export class Tokens {
    private collection: CollectionReference<DocumentData>;

    constructor() {
        this.collection = collection(fs, "tokens");
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    async get(
        id: string
    ): Promise<{ data: Token | null; cursor: DocumentSnapshot | null }> {
        const docRef = doc(fs, "tokens", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                data: { id: docSnap.id, ...docSnap.data() } as Token,
                cursor: docSnap
            };
        }

        return { data: null, cursor: null };
    }

    // deprecated
    async get2(id: string): Promise<Token | null> {
        const docRef = doc(fs, "tokens", id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists()
            ? ({ id: docSnap.id, ...docSnap.data() } as Token)
            : null;
    }

    // public feed: b-chest + boost
    async getChest(
        uid: string | null,
        currentCursor: DocumentSnapshot | null = null,
        direction: "next" | "prev" = "next"
    ): Promise<{ data: Token | null; cursor: DocumentSnapshot | null }> {
        // 1. Base Query: Sort by date descending (Newest first)
        let q = query(this.collection, orderBy("date", "desc"));

        if (uid) {
            q = query(q, where("to", "==", uid));
        }

        // 2. Handle Pagination for a single document
        if (currentCursor) {
            if (direction === "next") {
                // Get the single next older document
                q = query(q, startAfter(currentCursor), limit(1));
            } else {
                // Get the single next newer document
                q = query(q, endBefore(currentCursor), limitToLast(1));
            }
        } else {
            // Initial fetch: Get the most recent document
            q = query(q, limit(1));
        }

        const snap = await getDocs(q);

        if (snap.empty) {
            return { data: null, cursor: null };
        }

        const docSnap = snap.docs[0];
        const data = { id: docSnap.id, ...docSnap.data() } as Token;

        return {
            data,
            cursor: docSnap // This snapshot becomes the cursor for the next call
        };
    }

    // not used
    async getLatest(
        uid: string | null,
        currentCursor: DocumentSnapshot | null = null,
        direction: "next" | "prev" = "next"
    ): Promise<{
        data: Token[];
        cursor: {
            first: DocumentSnapshot | null;
            last: DocumentSnapshot | null;
        };
    }> {
        // 1. Build the base query
        let q = query(this.collection, orderBy("date", "desc"));

        if (uid) {
            q = query(q, where("to", "==", uid));
        }

        // 2. Apply Pagination Logic
        const pageSize = 10; // Adjust as needed

        if (currentCursor) {
            if (direction === "next") {
                // Get items older than the current last item
                q = query(q, startAfter(currentCursor), limit(pageSize));
            } else {
                // Get items newer than the current first item
                // We use limitToLast to maintain the "descending" visual order
                q = query(q, endBefore(currentCursor), limitToLast(pageSize));
            }
        } else {
            q = query(q, limit(pageSize));
        }

        const snap = await getDocs(q);

        const tokens = snap.docs.map(
            (doc) =>
                ({
                    id: doc.id,
                    ...doc.data()
                }) as Token
        );

        return {
            data: tokens,
            cursor: {
                first: snap.docs[0] || null,
                last: snap.docs[snap.docs.length - 1] || null
            }
        };
    }

    // public feed: b-gram and history
    async getRandom(uid: string): Promise<Token | null> {
        // Create a dummy document reference to get a random ID string
        const autoId = doc(this.collection).id;

        // Query for the first document whose ID is "greater than" our random ID
        const q = uid
            ? query(
                  this.collection,
                  where("to", "==", uid),
                  orderBy("__name__"), // __name__ refers to the Document ID
                  startAt(autoId),
                  limit(1)
              )
            : query(
                  this.collection,
                  orderBy("__name__"), // __name__ refers to the Document ID
                  startAt(autoId),
                  limit(1)
              );

        let snap = await getDocs(q);

        // Wrap around if we hit the end of the ID list
        if (snap.empty) {
            const wrapQuery = uid
                ? query(
                      this.collection,
                      where("to", "==", uid),
                      orderBy("__name__"),
                      limit(1)
                  )
                : query(this.collection, orderBy("__name__"), limit(1));
            snap = await getDocs(wrapQuery);
        }

        if (!snap.empty) {
            const docSnap = snap.docs[0];
            return { id: docSnap.id, ...docSnap.data() } as Token;
        }

        return null;
    }

    // public feed: b-chest
    async getRandomGram(userA: string, userB: string): Promise<Token | null> {
        // 1. Generate a random ID string using the SDK's internal generator
        const randomId = doc(this.collection).id;

        const createRandomQuery = (f: string, t: string) => {
            return query(
                this.collection,
                where("from", "==", f),
                where("to", "==", t),
                where("isAnonymous", "==", false),
                // Use the Document ID as the range filter
                where("__name__", ">=", randomId),
                orderBy("__name__"),
                limit(1)
            );
        };

        // 2. Fetch candidates from both directions
        let [snapA, snapB] = await Promise.all([
            getDocs(createRandomQuery(userA, userB)),
            getDocs(createRandomQuery(userB, userA))
        ]);

        let docA = snapA.docs[0];
        let docB = snapB.docs[0];

        // 3. Wrap-around logic: If no ID is >= the randomId, get the absolute first ID
        if (!docA) {
            const wrapA = await getDocs(
                query(
                    this.collection,
                    where("from", "==", userA),
                    where("to", "==", userB),
                    where("isAnonymous", "==", false),
                    orderBy("__name__"),
                    limit(1)
                )
            );
            docA = wrapA.docs[0];
        }

        if (!docB) {
            const wrapB = await getDocs(
                query(
                    this.collection,
                    where("from", "==", userB),
                    where("to", "==", userA),
                    where("isAnonymous", "==", false),
                    orderBy("__name__"),
                    limit(1)
                )
            );
            docB = wrapB.docs[0];
        }

        // 4. Randomly pick between the two directions
        const finalDoc =
            docA && docB
                ? Math.random() > 0.5
                    ? docA
                    : docB
                : docA || docB || null;

        return finalDoc
            ? ({ id: finalDoc.id, ...finalDoc.data() } as Token)
            : null;
    }

    // gem
    async getGram(
        userA: string,
        userB: string,
        currentCursor: DocumentSnapshot | null = null,
        direction: "next" | "prev" = "next"
    ): Promise<{ data: Token | null; cursor: DocumentSnapshot | null }> {
        const createQuery = (f: string, t: string) => {
            let q = query(
                this.collection,
                where("from", "==", f),
                where("to", "==", t),
                where("isAnonymous", "==", false),
                orderBy("date", "desc")
            );

            if (currentCursor) {
                if (direction === "next") {
                    // Moving forward in time (older items since order is desc)
                    q = query(q, startAfter(currentCursor), limit(1));
                } else {
                    // Moving backward in time (newer items)
                    // We use limitToLast to get the one immediately preceding the cursor
                    q = query(q, endBefore(currentCursor), limitToLast(1));
                }
            } else {
                q = query(q, limit(1));
            }
            return q;
        };

        const [snapA, snapB] = await Promise.all([
            getDocs(createQuery(userA, userB)),
            getDocs(createQuery(userB, userA))
        ]);

        const docA = snapA.docs[0];
        const docB = snapB.docs[0];

        let selectedDoc: DocumentSnapshot | null = null;

        if (docA && docB) {
            const dateA = docA.data().date;
            const dateB = docB.data().date;

            if (direction === "next") {
                // We want the most recent (largest) date among the "older" options
                selectedDoc = dateA > dateB ? docA : docB;
            } else {
                // We want the oldest (smallest) date among the "newer" options
                selectedDoc = dateA < dateB ? docA : docB;
            }
        } else {
            selectedDoc = docA || docB || null;
        }

        return {
            data: selectedDoc
                ? ({ id: selectedDoc.id, ...selectedDoc.data() } as Token)
                : null,
            cursor: selectedDoc
        };
    }

    // deprecated
    async getGram2(from: string, to: string): Promise<Token | null> {
        const q = query(
            this.collection,
            where("from", "==", from),
            where("to", "==", to),
            where("isAnonymous", "==", false),
            orderBy("date", "desc"),
            limit(1)
        );

        const snap = await getDocs(q);
        if (!snap.empty) {
            const docSnap = snap.docs[0];
            return { id: docSnap.id, ...docSnap.data() } as Token;
        }

        return null;
    }

    async give(
        to: string,
        isAnonymous: boolean,
        token: string,
        gold: string,
        jewel: string,
        diamond: string,
        duration: number,
        type: Token["type"],
        tip: number
    ): Promise<TokenResponse> {
        localStorage.setItem("tip", tip.toString());

        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/give" })).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "thank/give",
                    to,
                    isAnonymous,
                    token,
                    gold,
                    jewel,
                    diamond,
                    duration,
                    type,
                    tip
                })
            ).data as Token;

            localStorage.removeItem("tip");
            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("tip");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async rethank(
        isThank: boolean | null, // thank sender
        id: string,
        token: string,
        tip: number
    ): Promise<TokenResponse> {
        localStorage.setItem("tip", tip.toString());

        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/regive" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "thank/regive",
                    isThank,
                    id,
                    token,
                    tip
                })
            ).data as Token;

            localStorage.removeItem("tip");
            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("tip");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async addMiner(uid: string, id: string) {
        const docRef = doc(fs, "tokens", id);
        uid
            ? await updateDoc(docRef, {
                  digs: increment(1),
                  miners: arrayUnion(uid)
              })
            : await updateDoc(docRef, {
                  digs: increment(1),
                  diggers: arrayUnion(getDeviceID())
              });
    }

    // not allowed bc blockchain
    async remove(id: string) {
        const docRef = doc(fs, "tokens", id);
        await deleteDoc(docRef);
    }
}

export interface Kiito {
    id?: string; // doc id => b.333.eco/k/id

    gold: string; // thank you note
    jewel: string; // photo cover art // url to storage or stock
    diamond: string; // url to video in storage
    type: "photo" | "short" | "ai" | "stock"; // media type: video/mp4 or image/jpeg // store type in file storage

    karat?: number; // int: number of words
    carat?: number; //video length; photo = 1; no photo = 0;
    kiitos?: number; // total int

    duration?: number; // stored: short length in minutes; // good for analytics
    balance?: number; // not stored; for alert

    dateMinted?: Timestamp;
    from?: string; // minter

    dateMined?: Timestamp;
    to?: string; // miner // recipient can mine after // only 1; for multiple do b-chest

    redates?: Timestamp[];
    rethankers?: string[]; // uid
    retokens?: string[];
    rekiitos?: number[]; // kiitos
    iskiitos?: boolean[]; // todo later: is nearby
    isthanks?: boolean[]; // to sender or to recipient

    tip?: number; // total cents // always 0 initially
    jar?: number; // total cents // always 0 initially
    kitty?: number; // total cents // always 0 initially
    pool?: number; // total cents // always 0 initially
    retips?: number[]; // cents // always anonymous if not 0
    repools?: number[];

    thankerBalance?: number;
    fromBalance?: number;
    toBalance?: number;
    fromName?: string;
    toName?: string;

    // TODO: later add more fields for all stamp types: see Chests
    //latitude: number;
    //longitude: number;
    //address: string; // human readable address

    // stats: from here
    digs?: number; // total digs
    diggers?: string[]; // device id of diggers
    miners?: string[]; // uid of miners

    // stats: b app
    previews?: number;
    crawlers?: Array<string>;
    scans?: number; // total scans
    scanners?: string[]; // device id of scanners
}

type KiitoResponse = {
    text: string;
    color: "green" | "red" | "orange"; // status
    data?: Kiito;
    kiitos?: number;
};

export class Kiitos {
    private collection: CollectionReference<DocumentData>;

    constructor() {
        this.collection = collection(fs, "kiitos");
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    async get(id: string): Promise<Kiito | null> {
        const docRef = doc(fs, "kiitos", id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists()
            ? ({ id: docSnap.id, ...docSnap.data() } as Kiito)
            : null;
    }

    async mint(
        gold: string,
        jewel: string,
        diamond: string,
        duration: number,
        type: Kiito["type"]
    ): Promise<KiitoResponse> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/mint" })).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "thank/mint",
                    gold,
                    jewel,
                    diamond,
                    duration,
                    type
                })
            ).data as Kiito;

            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async mine(id: string): Promise<KiitoResponse> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/mine" })).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const kiitos = await verify({
                assertion,
                action: "thank/mine",
                id
            });

            return {
                text: "Success",
                color: "green",
                kiitos: kiitos.data as number
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async rethank(
        isThank: boolean | null, // thank sender
        id: string,
        token: string,
        tip: number
    ): Promise<KiitoResponse> {
        localStorage.setItem("tip", tip.toString());

        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "thank/rethank" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data = (
                await verify({
                    assertion,
                    action: "thank/rethank",
                    isThank,
                    id,
                    token,
                    tip
                })
            ).data as Kiito;

            localStorage.removeItem("tip");
            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("tip");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async addMiner(uid: string, id: string) {
        const docRef = doc(fs, "kiitos", id);
        uid
            ? await updateDoc(docRef, {
                  digs: increment(1),
                  miners: arrayUnion(uid)
              })
            : await updateDoc(docRef, {
                  digs: increment(1),
                  diggers: arrayUnion(getDeviceID())
              });
    }

    // not allowed bc blockchain
    async remove(id: string) {
        const docRef = doc(fs, "kiitos", id);
        await deleteDoc(docRef);
    }
}
